import { useSearchParams } from 'react-router-dom';

import { UnifiedConnectionDtoProvider } from '@/api/generated';
import useConnections from '@/hooks/Integrations/useConnections';
import useIntegrations from '@/hooks/Integrations/useIntegrations';
import { integrationsAuth } from '@/mappings/integrationsAuth';

const useIntegrationsAuth = () => {
  const { authenticateGoogle, authenticateMicrosoft } = useIntegrations();
  const [searchParams] = useSearchParams();
  const { createConnection } = useConnections();

  const sendMessageAndWaitForAck = (data: any) => {
    if (!window?.opener) {
      window.close();
      return;
    }

    window.opener.postMessage(data, window.origin);

    // Set up listener for ACK
    const ackHandler = (event: MessageEvent) => {
      if (event.origin !== window.origin) return;

      if (event.data.type === 'ACK') {
        window.removeEventListener('message', ackHandler);
        window.close();
      }
    };

    window.addEventListener('message', ackHandler);

    // Fallback timeout in case ACK never comes
    setTimeout(() => {
      window.removeEventListener('message', ackHandler);
      window.close();
    }, 5000); // 5 second timeout
  };

  const triggerGoogleConnection = () => {
    const code = searchParams.get(integrationsAuth.google.codeParam);
    if (code) {
      authenticateGoogle(code, integrationsAuth.google.redirectUrl).then(() => {
        sendMessageAndWaitForAck({ type: 'message' });
      });
    } else {
      const error = searchParams.get('error') || 'Google authentication failed';
      sendMessageAndWaitForAck({ type: 'message', error });
    }
  };

  const triggerMicrosoftConnection = () => {
    const code = searchParams.get(integrationsAuth.microsoft.codeParam);
    if (code) {
      authenticateMicrosoft(code, integrationsAuth.microsoft.redirectUrl).then(() => {
        sendMessageAndWaitForAck({ type: 'message' });
      });
    } else {
      const error = searchParams.get('error') || 'Microsoft authentication failed';
      sendMessageAndWaitForAck({ type: 'message', error });
    }
  };

  const triggerUnifiedConnection = (provider: UnifiedConnectionDtoProvider) => {
    const connectionId = searchParams.get('id');
    if (connectionId) {
      createConnection({
        connectionId: connectionId,
        provider: provider
      }).then(() => {
        sendMessageAndWaitForAck({ type: 'message' });
      });
    } else {
      const error = searchParams.get('error') || 'Unified authentication failed';
      sendMessageAndWaitForAck({ type: 'message', error });
    }
  };

  return { triggerGoogleConnection, triggerMicrosoftConnection, triggerUnifiedConnection };
};

export default useIntegrationsAuth;
